import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import {
    ButtonWorkspaceUserDropdown,
    ButtonPrimary,
    UserContext,
    ButtonSecondary,
    ButtonWorkspaceDropdown,
    ButtonTertiary,
} from "@nova/component-btn"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectAllMembers } from "features/entity-caching/members/UserManagementSlice"
import { useCallback, useMemo, useState } from "react"
import { WorkspaceCollaboratorProfile } from "../../workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useTranslation } from "@nova/use-translation"
import {
    selectAddOnsById,
    selectAvailableAddOnsCounts,
    selectAvailableLicenseCounts,
    selectLicenseById,
    selectSubscriptionModalContext,
    selectUnassignedAddOns,
    selectUnassignedLicenses,
} from "features/entity-caching/licenses/selectors"
import * as Icons from "@nova/component-icons"
import { updateLicense } from "features/entity-caching/licenses/thunks"
import { get } from "nodash"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { formatSubscriptionTitle, formatSubscriptionType } from "../module"
import { useAsyncCallback } from "@nova/use-async-callback"
import { AddOns, Licenses, SubscriptionsType } from "@nova/constants"
import { hideGlobalModal } from "runlevel/actions"
import { LicenseRecord, WorkspaceMember } from "@nova/schemas"
import { DiscriminatedUnion } from "@nova/util-ts"

export const AssignSubscriptionsModal = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const modalContext = useAppSelector(selectSubscriptionModalContext())
    const { subscriptionModalId, isMultiAssign, isAddOn, isTransferring } = modalContext

    const unassignedLicenses = useAppSelector(selectUnassignedLicenses)
    const unassignedAddOns = useAppSelector(selectUnassignedAddOns)

    const license = useAppSelector(selectLicenseById(subscriptionModalId || ""))
    const addOn = useAppSelector(selectAddOnsById(subscriptionModalId || ""))

    const [menuOpen, setMenuOpen] = useState(false)

    const licenseCounts = useAppSelector(selectAvailableLicenseCounts)
    const addOnCounts = useAppSelector(selectAvailableAddOnsCounts)

    const [stagedSubscriptionIds, setStagedSubscriptionIds] = useState<Set<string>>(new Set())
    const [duplicateSubscriptionIds, setDuplicateSubsriptionIds] = useState<Set<string>>(new Set())

    const [assignedSubscriptions, setAssignedSubscriptions] = useState<
        {
            user: UserContext
            subscription: LicenseRecord
        }[]
    >([])

    const totalLicenseCounts = useMemo(() => {
        const counts = { ...licenseCounts }
        assignedSubscriptions.forEach((sub) => {
            if (sub.subscription.isAddOn) return
            const type = sub.subscription.subscriptionType as Licenses
            counts[type] = counts[type] - 1
        })
        return counts
    }, [licenseCounts, assignedSubscriptions])

    const totalAddOnCounts = useMemo(() => {
        const counts = { ...addOnCounts }
        assignedSubscriptions.forEach((sub) => {
            if (!sub.subscription.isAddOn) return
            const type = sub.subscription.subscriptionType as AddOns
            counts[type] = counts[type] - 1
        })
        return counts
    }, [assignedSubscriptions, addOnCounts])

    const allMembers = useAppSelector(selectAllMembers())
    const dropdownButtons = useMemo(() => {
        const allMembersArray = Object.values(
            allMembers
        ).flat() as DiscriminatedUnion<WorkspaceMember>[]
        const seen = new Set<string>()
        const uniqueMembers = allMembersArray.filter((item) => {
            if (seen.has(item.userId)) {
                return false
            }
            seen.add(item.userId)
            return true
        })
        return uniqueMembers.map((member) => {
            return {
                userId: member.userId,
                fullName: member.fullName,
                email: member.email || member.userId,
                CollaboratorComponent: (
                    <WorkspaceCollaboratorProfile
                        userId={member.userId}
                        email={member.email}
                        fullName={member.fullName}
                    />
                ),
            }
        })
    }, [allMembers])

    const subscriptionDropdownButtons = useMemo(() => {
        if (!isMultiAssign) return []
        const buttons: { label: string; subLabel: string; onClick: () => void }[] = []
        if (isAddOn) {
            Object.values(AddOns).forEach((addOn) => {
                if (totalAddOnCounts[addOn] === 0) return
                buttons.push({
                    label: formatSubscriptionType(addOn),
                    subLabel: `${totalAddOnCounts[addOn]} remain`,
                    onClick: () => setStagedSubscriptionType(addOn),
                })
            })
            return buttons
        }

        Object.values(Licenses).forEach((license) => {
            if (license === Licenses.Lite || totalLicenseCounts[license] === 0) return
            buttons.push({
                label: formatSubscriptionType(license),
                subLabel: `${totalLicenseCounts[license]} remain`,
                onClick: () => setStagedSubscriptionType(license),
            })
        })
        return buttons
    }, [totalAddOnCounts, totalLicenseCounts, isMultiAssign, isAddOn])

    const [stagedSubscriptionType, setStagedSubscriptionType] = useState<
        SubscriptionsType | undefined
    >(
        license?.subscriptionType ||
            addOn?.subscriptionType ||
            (subscriptionDropdownButtons[0]?.label as SubscriptionsType)
    )

    const addUserToReview = useCallback(
        (user: UserContext) => {
            const userToReview = user
            if (!userToReview || !stagedSubscriptionType) return

            if (!isMultiAssign) {
                if (!license && !addOn) return
                setAssignedSubscriptions([{ user: userToReview, subscription: license! || addOn! }])
                return
            }
            const availableSubscription = !isAddOn
                ? unassignedLicenses.find(
                      (license) =>
                          license.subscriptionType === stagedSubscriptionType &&
                          !stagedSubscriptionIds.has(license.licenseId)
                  )
                : unassignedAddOns.find(
                      (addOn) =>
                          addOn.subscriptionType === stagedSubscriptionType &&
                          !stagedSubscriptionIds.has(addOn.licenseId)
                  )

            if (!availableSubscription) return
            if (
                !isMultiAssign &&
                assignedSubscriptions.find((sub) => sub.user.userId === userToReview.userId)
            )
                return

            setStagedSubscriptionIds(stagedSubscriptionIds.add(availableSubscription.licenseId))
            setAssignedSubscriptions((prevAssigned) => {
                return [
                    ...prevAssigned,
                    { user: userToReview, subscription: availableSubscription },
                ]
            })
        },
        [
            stagedSubscriptionIds,
            stagedSubscriptionType,
            assignedSubscriptions,
            isMultiAssign,
            isAddOn,
            addOn,
            license,
            unassignedAddOns,
            unassignedLicenses,
        ]
    )

    const removeUserFromReview = useCallback(
        (userId: string, licenseId: string) => {
            const filteredUsers = assignedSubscriptions.filter(
                (sub) =>
                    (sub.user.userId !== userId || sub.user.fullName !== userId) &&
                    sub.subscription.licenseId !== licenseId
            )
            setAssignedSubscriptions(filteredUsers)
            stagedSubscriptionIds.delete(licenseId)
            setStagedSubscriptionIds(stagedSubscriptionIds)
            duplicateSubscriptionIds.delete(licenseId)
            setDuplicateSubsriptionIds(duplicateSubscriptionIds)
        },
        [assignedSubscriptions, stagedSubscriptionIds, duplicateSubscriptionIds]
    )

    const { assignSubscriptions, loading: isLoading } = useAsyncCallback(
        {
            assignSubscriptions: async () => {
                const results = await Promise.all(
                    assignedSubscriptions.map((sub) =>
                        dispatch(
                            updateLicense({
                                ownerId: sub.subscription.ownerId,
                                licenseId: sub.subscription.licenseId,
                                attributes: { assigneeId: sub.user.userId || sub.user.fullName },
                            })
                        ).catch()
                    )
                )
                let hadDuplicateSubscriptions = false
                results.forEach((res) => {
                    const hasError = get(res, "error")
                    const rejectionReason = get(res, "meta.rejectionReason")
                    const payload = get(res, "payload") as string

                    if (!hasError) return
                    if (rejectionReason !== "duplicateSubscription") return

                    hadDuplicateSubscriptions = true
                    setDuplicateSubsriptionIds(duplicateSubscriptionIds.add(payload))
                })

                if (hadDuplicateSubscriptions) {
                    dispatch(
                        setGlobalNotificationContext({
                            status: GlobalNotificationStatus.error,
                            message: `Failed to assign ${isAddOn ? "Add-on" : "License"}, please try again.`,
                            timeoutInMs: 3000,
                        })
                    )
                    return
                }
                dispatch(
                    setGlobalNotificationContext({
                        status: GlobalNotificationStatus.success,
                        message: `${isAddOn ? "Add-on" : "License"} assigned successfully.`,
                        timeoutInMs: 3000,
                    })
                )
                dispatch(hideGlobalModal())
            },
        },
        [assignedSubscriptions, duplicateSubscriptionIds, isAddOn, dispatch]
    )

    const modalTitle = () => {
        if (isTransferring) {
            if (isAddOn && addOn)
                return `Transfer ${formatSubscriptionType(addOn.subscriptionType, true)} Add-on`
            else if (license)
                return `Transfer ${formatSubscriptionType(license.subscriptionType, true)} License`
        }
        return `Assign ${formatSubscriptionTitle(isAddOn, isMultiAssign)}`
    }
    const modalText = () => {
        if (isTransferring) {
            if (isAddOn && addOn)
                return `Choose a person to transfer the ${formatSubscriptionType(addOn.subscriptionType, true)} Add-on to.`
            else if (license)
                return `Choose a person to transfer the ${formatSubscriptionType(license.subscriptionType, true)} License to.`
        }
        return `Assign your ${formatSubscriptionTitle(isAddOn, isMultiAssign)} to people to grant them access.`
    }

    // const disableAssignment = isAddOn
    //     ? totalAddOnCounts[stagedSubscriptionType as AddOns] === 0
    //     : totalLicenseCounts[stagedSubscriptionType as Licenses] === 0
    return (
        <HighPriorityNovaModalContainer
            title={modalTitle()}
            icon={isTransferring ? Icons.PersonEdit : Icons.PersonAdd}
            className="w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                    <ButtonPrimary
                        title={
                            isTransferring
                                ? `Transfer ${isAddOn ? "Add-on" : "License"}`
                                : "Confirm"
                        }
                        onClick={assignSubscriptions}
                        isLoading={isLoading}
                        disabled={isLoading || assignedSubscriptions.length === 0}
                    />
                </div>
            }
            closeModal={() => dispatch(hideGlobalModal())}
        >
            <h5 className="mb-4">{t(modalText())}</h5>
            <h5 className="text-gray-100 mb-2">{t("Assign to")}</h5>
            <div className="flex items-center gap-2">
                <ButtonWorkspaceUserDropdown
                    userDropdownButtons={dropdownButtons}
                    widthClass={isMultiAssign ? "w-[17.813rem]" : "w-[25.75rem]"}
                    setSelectedUser={(user) => {
                        addUserToReview(user)
                    }}
                />
                {isMultiAssign && (
                    <ButtonWorkspaceDropdown
                        dropdownButtons={subscriptionDropdownButtons}
                        label={stagedSubscriptionType || ""}
                        menuOpen={menuOpen && subscriptionDropdownButtons.length !== 0}
                        setMenuOpen={setMenuOpen}
                        widthClass={"w-[7.5rem]"}
                        dropdownWidthClass={"w-[10rem]"}
                        disabled={subscriptionDropdownButtons.length === 0}
                    />
                )}
            </div>
            {/* {disableAssignment &&
                isMultiAssign &&
                stagedSubscriptionType &&
                assignedSubscriptions.length > 1 && (
                    <div className="flex items-center gap-2 text-orange-500 mt-3">
                        <Icons.Warning />
                        <h5>
                            {t(
                                `All available ${formatSubscriptionType(stagedSubscriptionType, true)} ${isAddOn ? "Add-ons" : "Licenses"} have been assigned.`
                            )}
                        </h5>
                    </div>
                )} */}
            {assignedSubscriptions.length > 0 && (
                <>
                    <h5 className="!text-left mt-3 text-gray-100">
                        {t(`Review assigned ${isAddOn ? "Add-ons" : "Licenses"}`)}
                    </h5>
                    <ScrollableArea className="max-h-[18.438rem] flex flex-col gap-4 mt-2 p-6 py-4 border rounded-md border-gray-700">
                        {assignedSubscriptions?.map((assigned) => {
                            return (
                                <AssignedSubscription
                                    key={assigned.subscription.licenseId}
                                    user={assigned.user}
                                    type={assigned.subscription.subscriptionType}
                                    removeUserFromReview={(userId) =>
                                        removeUserFromReview(
                                            userId,
                                            assigned.subscription.licenseId
                                        )
                                    }
                                    hasError={duplicateSubscriptionIds.has(
                                        assigned.subscription.licenseId
                                    )}
                                />
                            )
                        })}
                    </ScrollableArea>
                </>
            )}
            {duplicateSubscriptionIds.size > 0 && (
                <div className="text-red-500 flex items-center gap-1 mt-2">
                    <Icons.Error />
                    <h5>
                        {t(
                            isAddOn
                                ? "Some people already have these Add-ons assigned to them."
                                : "Some people already have these Licenses assigned to them."
                        )}
                    </h5>
                </div>
            )}
        </HighPriorityNovaModalContainer>
    )
}

const AssignedSubscription = ({
    user,
    type,
    hasError,
    removeUserFromReview,
}: {
    user: UserContext
    type: SubscriptionsType
    hasError?: boolean
    removeUserFromReview: (userId: string) => void
}) => {
    const { t } = useTranslation()
    const [isHovering, setIsHovering] = useState(false)
    return (
        <div
            className="flex justify-between items-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <WorkspaceCollaboratorProfile
                userId={user.userId}
                email={user.email}
                fullName={user.fullName}
            />
            {!isHovering && (
                <div className="flex items-center gap-2 relative">
                    {hasError && (
                        <Icons.SmallIcon Icon={Icons.Error} className="text-red-500 mt-0.5" />
                    )}
                    <h5>{t(formatSubscriptionType(type, true))}</h5>
                </div>
            )}
            {isHovering && (
                <ButtonSecondary
                    title="Remove"
                    onClick={() => removeUserFromReview(user.userId || user.fullName || "")}
                    className="-mr-2"
                />
            )}
        </div>
    )
}
